import React, { useState } from 'react'
import { Button } from './ui/button'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from './ui/card'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select'
import { RadioGroup, RadioGroupItem } from './ui/radio-group'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog'
import {
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
} from './ui/table'

const SAMPLE_ROWS = [
  { ticker: 'AAPL', name: 'Apple Inc.', price: '$182.35', change: '+1.2%', status: 'Buy' },
  { ticker: 'MSFT', name: 'Microsoft',  price: '$415.10', change: '+0.8%', status: 'Hold' },
  { ticker: 'NVDA', name: 'NVIDIA',     price: '$875.40', change: '+3.1%', status: 'Buy' },
  { ticker: 'GOOGL', name: 'Alphabet',  price: '$178.20', change: '-0.4%', status: 'Hold' },
]

function Section({ title, children }) {
  return (
    <section className="mb-10">
      <h2 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-4">
        {title}
      </h2>
      {children}
    </section>
  )
}

export default function Demo() {
  const [radioValue, setRadioValue] = useState('paper')

  return (
    <div className="min-h-screen bg-[#F9FAFB] font-sans p-8">
      <div className="max-w-3xl mx-auto">

        {/* Header */}
        <div className="mb-10">
          <h1 className="font-display text-2xl font-bold text-[#101828] mb-1">
            UI Component Demo
          </h1>
          <p className="text-sm text-muted-foreground">
            shadcn/ui overrides — Figma palette (Inter + Onest)
          </p>
        </div>

        {/* ── Buttons ────────────────────────────────────────────────────────── */}
        <Section title="Buttons">
          <div className="flex flex-wrap items-center gap-3">
            <Button>Primary</Button>
            <Button variant="secondary">Secondary</Button>
            <Button variant="destructive">Destructive</Button>
            <Button variant="outline">Outline</Button>
            <Button variant="ghost">Ghost</Button>
            <Button disabled>Disabled</Button>
            <Button size="sm">Small</Button>
            <Button size="lg">Large</Button>
          </div>
        </Section>

        {/* ── Cards ──────────────────────────────────────────────────────────── */}
        <Section title="Cards">
          <div className="grid grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle>Portfolio Value</CardTitle>
                <CardDescription>As of today, 09:30 ET</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold text-[#101828]">$104,553</p>
                <p className="text-sm text-success mt-1">+4.55% all time</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Win Rate</CardTitle>
                <CardDescription>Last 512 trades</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold text-[#101828]">88.7%</p>
                <p className="text-sm text-muted-foreground mt-1">Goal ≥ 60%</p>
              </CardContent>
            </Card>
          </div>
        </Section>

        {/* ── Inputs ─────────────────────────────────────────────────────────── */}
        <Section title="Input (bottom-border style)">
          <Card>
            <CardContent className="pt-6">
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-1">
                  <Label htmlFor="demo-email">Email</Label>
                  <Input id="demo-email" type="email" placeholder="you@example.com" />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="demo-capital">Capital (USD)</Label>
                  <Input id="demo-capital" type="number" placeholder="100 000" />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="demo-disabled">Disabled</Label>
                  <Input id="demo-disabled" placeholder="not editable" disabled />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="demo-strategy">Strategy</Label>
                  <Select>
                    <SelectTrigger id="demo-strategy">
                      <SelectValue placeholder="Select strategy" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mr">MeanReversion</SelectItem>
                      <SelectItem value="mh">MomentumHold</SelectItem>
                      <SelectItem value="be">Base-Extended</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
        </Section>

        {/* ── Radio Group ────────────────────────────────────────────────────── */}
        <Section title="RadioGroup">
          <Card>
            <CardContent className="pt-6">
              <RadioGroup value={radioValue} onValueChange={setRadioValue} className="flex gap-6">
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="paper" id="paper" />
                  <Label htmlFor="paper">Paper Trading</Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="live" id="live" />
                  <Label htmlFor="live">Live Trading</Label>
                </div>
              </RadioGroup>
              <p className="text-xs text-muted-foreground mt-3">
                Selected: <strong>{radioValue}</strong>
              </p>
            </CardContent>
          </Card>
        </Section>

        {/* ── Dialog ─────────────────────────────────────────────────────────── */}
        <Section title="Dialog">
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="secondary">Open Dialog</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Confirm Switch to Live</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-muted-foreground">
                You've completed the mandatory 14-day paper period. Switch to live trading?
              </p>
              <div className="flex justify-end gap-3 mt-4">
                <DialogTrigger asChild>
                  <Button variant="secondary">Cancel</Button>
                </DialogTrigger>
                <Button>Confirm</Button>
              </div>
            </DialogContent>
          </Dialog>
        </Section>

        {/* ── Table ──────────────────────────────────────────────────────────── */}
        <Section title="Table">
          <Card className="overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Ticker</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead>Change</TableHead>
                  <TableHead>Signal</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {SAMPLE_ROWS.map(row => (
                  <TableRow key={row.ticker}>
                    <TableCell className="font-semibold">{row.ticker}</TableCell>
                    <TableCell className="text-muted-foreground">{row.name}</TableCell>
                    <TableCell>{row.price}</TableCell>
                    <TableCell className={row.change.startsWith('+') ? 'text-success' : 'text-destructive'}>
                      {row.change}
                    </TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        row.status === 'Buy'
                          ? 'bg-green-50 text-green-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {row.status}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </Section>

      </div>
    </div>
  )
}
